import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import productService from '../../services/productService';
import basketService from '../../services/basketService';
import bannerService from '../../services/bannerService';
import { uploadFile, UPLOAD_FOLDERS } from '../../services/uploadService';
import {
  BANNER_TYPE,
  BANNER_TYPE_OPTIONS,
  PORTAL_SCOPE,
  PORTAL_SCOPE_OPTIONS,
  buildBasketSelectOptions,
  buildCreatePayload,
  buildProductSelectOptions,
  extractBasketsFromListResponse,
} from './banners.helpers';

function ImageUploadField({ label, required, file, preview, onSelect, onClear }) {
  return (
    <div className="mb-4">
      <p className="mb-2 text-sm font-medium text-gray-700">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </p>
      {preview ? (
        <div className="mb-2">
          <img
            src={preview}
            alt=""
            className="max-h-32 rounded border border-gray-200 object-contain"
          />
        </div>
      ) : null}
      <Space wrap>
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={(f) => {
            onSelect(f);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />}>
            {file ? 'Replace image' : 'Choose image'}
          </Button>
        </Upload>
        {file ? (
          <Button type="link" onClick={onClear}>
            Clear
          </Button>
        ) : null}
      </Space>
      {file ? (
        <p className="mt-1 text-xs text-gray-500">{file.name}</p>
      ) : null}
    </div>
  );
}

export default function BannerCreateDrawer({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [baskets, setBaskets] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingBaskets, setLoadingBaskets] = useState(false);
  const [webFile, setWebFile] = useState(null);
  const [mobileFile, setMobileFile] = useState(null);
  const [webPreview, setWebPreview] = useState('');
  const [mobilePreview, setMobilePreview] = useState('');

  const bannerType = Form.useWatch('banner_type', form);

  const productOptions = useMemo(
    () => buildProductSelectOptions(products),
    [products],
  );

  const basketOptions = useMemo(
    () => buildBasketSelectOptions(baskets),
    [baskets],
  );

  useEffect(() => {
    if (!open) return;

    form.resetFields();
    setWebFile(null);
    setMobileFile(null);
    setWebPreview('');
    setMobilePreview('');

    setLoadingProducts(true);
    productService
      .getProducts({
        type: 'PRE_IPO',
        page: 1,
        limit: 200,
        sort_by: 'preference_index',
        sort_order: 'asc',
      })
      .then((res) => {
        const list = res?.data?.products ?? res?.products ?? [];
        setProducts(Array.isArray(list) ? list : []);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));

    setLoadingBaskets(true);
    basketService
      .listBaskets({ page: 1, limit: 100, status: 'ACTIVE' })
      .then((res) => setBaskets(extractBasketsFromListResponse(res)))
      .catch(() => setBaskets([]))
      .finally(() => setLoadingBaskets(false));
  }, [open, form]);

  useEffect(() => {
    if (bannerType === BANNER_TYPE.BASKET_PROMOTIONAL) {
      form.setFieldValue('portal_scope', PORTAL_SCOPE.PARTNER);
    }
  }, [bannerType, form]);

  const handleWebSelect = (file) => {
    setWebFile(file);
    setWebPreview(URL.createObjectURL(file));
  };

  const handleMobileSelect = (file) => {
    setMobileFile(file);
    setMobilePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!webFile) {
        message.error('Please upload a web banner image.');
        return;
      }
      if (!mobileFile) {
        message.error('Please upload a mobile banner image.');
        return;
      }

      setSubmitting(true);

      const [webResult, mobileResult] = await Promise.all([
        uploadFile(webFile, UPLOAD_FOLDERS.BANNERS),
        uploadFile(mobileFile, UPLOAD_FOLDERS.BANNERS),
      ]);

      if (!webResult.success) {
        message.error(webResult.message || 'Web image upload failed.');
        return;
      }
      if (!mobileResult.success) {
        message.error(mobileResult.message || 'Mobile image upload failed.');
        return;
      }

      const payload = buildCreatePayload(values, {
        web: webResult.url,
        mobile: mobileResult.url,
      });

      await bannerService.createBanner(payload);
      message.success('Banner created.');
      onCreated();
      onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || err?.message || 'Failed to create banner.');
    } finally {
      setSubmitting(false);
    }
  };

  const portalScopeOptions =
    bannerType === BANNER_TYPE.BASKET_PROMOTIONAL
      ? PORTAL_SCOPE_OPTIONS.filter((o) => o.value === PORTAL_SCOPE.PARTNER)
      : PORTAL_SCOPE_OPTIONS;

  const showProductField =
    bannerType === BANNER_TYPE.PROMOTIONAL || bannerType === BANNER_TYPE.FESTIVE;
  const showBasketField = bannerType === BANNER_TYPE.BASKET_PROMOTIONAL;
  const showRedirectField = bannerType === BANNER_TYPE.EXTERNAL;
  const showDetails = Boolean(bannerType);

  return (
    <Drawer
      title="New banner"
      width={520}
      open={open}
      destroyOnClose
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="banner_type"
          label="What are you creating?"
          rules={[{ required: true, message: 'Select a banner type' }]}
        >
          <Radio.Group className="flex flex-col gap-2 w-full">
            {BANNER_TYPE_OPTIONS.map((opt) => (
              <Radio key={opt.value} value={opt.value} className="items-start! whitespace-normal!">
                <span className="font-medium">{opt.label}</span>
                <p className="text-xs text-gray-500 mt-0.5 ml-0">{opt.description}</p>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        {showDetails ? (
          <>
            <Form.Item
              name="name"
              label="Banner name"
              rules={[{ required: true, message: 'Enter a banner name' }]}
            >
              <Input placeholder="e.g. Summer Pre-IPO Promo" maxLength={255} />
            </Form.Item>

            <Form.Item
              name="portal_scope"
              label="Portal scope"
              rules={[{ required: true, message: 'Select portal scope' }]}
            >
              <Select
                placeholder="Select portal"
                options={portalScopeOptions}
                disabled={bannerType === BANNER_TYPE.BASKET_PROMOTIONAL}
              />
            </Form.Item>

            {showProductField ? (
              <Form.Item
                name="product_id"
                label="Product"
                rules={
                  bannerType === BANNER_TYPE.PROMOTIONAL
                    ? [{ required: true, message: 'Select a product' }]
                    : []
                }
              >
                <Select
                  placeholder={
                    bannerType === BANNER_TYPE.FESTIVE
                      ? 'Optional — link to a product'
                      : 'Select Pre-IPO product'
                  }
                  showSearch
                  allowClear={bannerType === BANNER_TYPE.FESTIVE}
                  loading={loadingProducts}
                  optionFilterProp="label"
                  options={productOptions}
                />
              </Form.Item>
            ) : null}

            {showBasketField ? (
              <Form.Item
                name="basket_id"
                label="Basket"
                rules={[{ required: true, message: 'Select a basket' }]}
              >
                <Select
                  placeholder="Select basket"
                  showSearch
                  loading={loadingBaskets}
                  optionFilterProp="label"
                  options={basketOptions}
                />
              </Form.Item>
            ) : null}

            {showRedirectField ? (
              <Form.Item
                name="redirect_url"
                label="Redirect URL"
                rules={[
                  { required: true, message: 'Enter redirect URL' },
                  { type: 'url', message: 'Enter a valid URL' },
                ]}
              >
                <Input placeholder="https://example.com/webinar" />
              </Form.Item>
            ) : null}

            <ImageUploadField
              label="Web banner image"
              required
              file={webFile}
              preview={webPreview}
              onSelect={handleWebSelect}
              onClear={() => {
                setWebFile(null);
                setWebPreview('');
              }}
            />

            <ImageUploadField
              label="Mobile banner image"
              required
              file={mobileFile}
              preview={mobilePreview}
              onSelect={handleMobileSelect}
              onClear={() => {
                setMobileFile(null);
                setMobilePreview('');
              }}
            />
          </>
        ) : null}
      </Form>
    </Drawer>
  );
}
